import { useEffect, useMemo, useRef, useState } from "react";
import { GripVertical, RotateCcw } from "lucide-react";

/**
 * Drag-and-drop ordered list of category names.
 *
 *   value: string[]            — current ordered category list
 *   onChange: (next) => void   — emits the new order
 *   available: string[]        — categories discovered from current services (used to suggest + merge)
 */
export default function CategoryOrderList({ value = [], onChange, available = [] }) {
    const [items, setItems] = useState(value);
    const dragIndex = useRef(null);
    const [over, setOver] = useState(null);

    // Merge available categories: keep current order, append unknown categories to the end
    const merged = useMemo(() => {
        const seen = new Set(items.map((c) => c.toLowerCase()));
        const extras = available.filter((c) => !seen.has(c.toLowerCase()));
        return [...items, ...extras];
    }, [items, available]);

    useEffect(() => {
        setItems(value);
    }, [value]);

    const emit = (next) => {
        setItems(next);
        onChange?.(next);
    };

    const handleDragStart = (idx) => (e) => {
        dragIndex.current = idx;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
    };
    const handleDragOver = (idx) => (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(idx);
    };
    const handleDrop = (idx) => (e) => {
        e.preventDefault();
        const from = dragIndex.current;
        dragIndex.current = null;
        setOver(null);
        if (from === null || from === idx) return;
        const next = [...merged];
        const [moved] = next.splice(from, 1);
        next.splice(idx, 0, moved);
        emit(next);
    };
    const handleDragEnd = () => {
        dragIndex.current = null;
        setOver(null);
    };

    const resetToAlpha = () => {
        const alpha = [...merged].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        emit(alpha);
    };

    if (merged.length === 0) {
        return (
            <div className="text-xs text-[#8A6574] italic px-3 py-4 border border-dashed border-[#6B1535]/15" data-testid="category-order-empty">
                No service categories yet. Add a service first to start ordering categories.
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid="category-order-list">
            {merged.map((cat, idx) => (
                <div
                    key={cat}
                    draggable
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver(idx)}
                    onDrop={handleDrop(idx)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 px-3 py-2.5 bg-[#FBF6F0] border cursor-grab active:cursor-grabbing transition-colors ${over === idx ? "border-[#C29A5B]" : "border-[#6B1535]/15 hover:border-[#6B1535]/20"}`}
                    data-testid={`category-order-row-${cat}`}
                >
                    <GripVertical size={14} className="text-[#8A6574] flex-shrink-0" />
                    <span className="text-[0.6rem] tracking-[0.2em] uppercase text-[#8F622C] w-6 flex-shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-[#3D0C1F] flex-1 truncate">{cat}</span>
                </div>
            ))}
            <div className="pt-2">
                <button
                    type="button"
                    onClick={resetToAlpha}
                    className="inline-flex items-center gap-2 text-[0.6rem] tracking-[0.2em] uppercase text-[#7A5262] hover:text-[#8F622C] transition-colors"
                    data-testid="category-order-reset"
                >
                    <RotateCcw size={11} /> Reset to alphabetical
                </button>
            </div>
        </div>
    );
}
